import React from "react";
import type { Event } from "../../../../../../api/useEvents";
import createIOSortType from "../../../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Ellipsis from "@ui/Ellipsis";
import { css } from "@linaria/core";
import { color, fonts } from "@ui";
import { colors, spacings } from "@ui/Theme";
import type { Project } from "../../../../../../api/useProjects";
import { useTranslations } from "../../../../../../api/useTranslations";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import type { SortableValue } from "diagnostics-lib/comparator";
import formatDate from "@ui/i18n/formatDate";
import { useLocation, useNavigate } from "react-router-dom";
import Text from "@ui/Text";
import t from "../../../../../../i18n";
import { millisecondsToFractional } from "../../../utils/millisecondsToFractional";
import TooltipsV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { useComments } from "../../../../../../api/useComments";
import { analysisTitleShort } from "../../../../Analysis/analysisTitle";
import { DEFAULT_CONFIG, Markdown } from "@ui/Markdown";
import { ChatFollowUpLine } from "@ui/IconV2/react";
import {
  getComputeRowKey,
  getEventDetection,
  getEventSystems,
  isMultiFaultEvent,
} from "../../../../../../domain/event";
import { gotoEventPage } from "../../../utils/event";
import { toBasenamePath } from "../../../../../../router/helpers/navigation";
import { Tag } from "@ui/Tag";
import { EventName } from "../../../components/EventName";

/** Variables */
const columns = [
  "name",
  "startsAt",
  "closedAt",
  "detection",
  "system",
  "reparability",
  "lastComment",
] as const;
type Column = (typeof columns)[number];

/** CreateGlobalState */
const useSortClosedEventsInput = createGlobalState({
  storage: "session",
  key: "useSortClosedEventsInput",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

/** Type */
interface P {
  project: Project;
  events: Event[];
  loading: boolean;
}

const computeRowKey = (event: Event) => getComputeRowKey(event);

const ClosedEventTable = ({ project, events, loading }: P) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const translations = useTranslations();

  const comments = useComments(project.id);
  const lastCommentsByEvent = React.useMemo(
    () =>
      comments
        .filter(
          (
            comment,
          ): comment is typeof comment & { faultEvent: NonNullable<typeof comment.faultEvent> } =>
            Boolean(comment.faultEvent),
        )
        .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
        .reduce<Record<string, Omit<(typeof comments)[number], "faultEvent">>>((acc, comment) => {
          const { faultEvent, ...commentBase } = comment;
          acc[faultEvent.id] = commentBase;
          return acc;
        }, {}),
    [comments],
  );
  const hiddenReparabilty = React.useMemo(
    () => events.every(e => e.reparability === null),
    [events],
  );
  const [sort, setSort] = useSortClosedEventsInput({ scope: project.id });

  const sortSelector: Record<Column, (e: Event) => SortableValue | null> = {
    name: e => e.name,
    startsAt: e => {
      const duration = new Date().getTime() - new Date(e.startsAt).getTime();
      return duration;
    },
    closedAt: e => {
      if (!e.closedAt) throw new Error("closedAt cannot be undefined in the closed table");
      const duration = new Date().getTime() - new Date(e.closedAt).getTime();
      return duration;
    },
    detection: e => getEventDetection(e),
    system: e =>
      getEventSystems(e)
        .sort((a, b) => a.localeCompare(b))
        .join(", "),
    reparability: e => e.reparability,
    lastComment: () => null,
  };

  const sortedDatas = useSortedData(events, sortSelector[sort.column], sort.direction);
  const metadata: Record<Column, ColumnMetaData<Event>> = React.useMemo(
    () => ({
      name: {
        label: t("project.fault_events.table.closed.metadata.name"),
        render: (b: Event) => (
          <div className={nameWrapper}>
            <EventName name={b.name} hasMultiFault={isMultiFaultEvent(b)} />
            {b.type === "MANUAL" && <Tag size="s">{t("project.event.type.manual")}</Tag>}
          </div>
        ),
        minWidth: "300px",
        sortable: true,
        sticky: true,
        stickyBorder: true,
      },
      system: {
        label: (
          <span className={lineClamp}>
            {t("project.fault_events.table.closed.metadata.system")}
          </span>
        ),
        render: (event: Event) => {
          const systems = getEventSystems(event).sort().join(", ");
          return systems ? <Ellipsis title={systems}>{systems}</Ellipsis> : null;
        },
        minWidth: "150px",
        maxWidth: "150px",
        sortable: true,
      },
      detection: {
        label: (
          <span className={lineClamp}>
            {t("project.fault_events.table.closed.metadata.detection")}
          </span>
        ),
        render: (event: Event) => {
          const detection = getEventDetection(event);
          return detection === null ? null : (
            <Text>{t("project.fault_events.tab.closed.detection", detection)}</Text>
          );
        },
        minWidth: "135px",
        maxWidth: "135px",
        sortable: true,
      },
      startsAt: {
        label: (
          <span className={lineClamp}>
            {t("project.fault_events.table.closed.metadata.start_date")}
          </span>
        ),
        render: (b: Event) => {
          const duration = new Date().getTime() - new Date(b.startsAt).getTime();
          const fractional = millisecondsToFractional(duration);
          const date = formatDate(b.startsAt, "date", project.timezone);
          return (
            <div className={datetime}>
              <span>{date}</span>
              <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
            </div>
          );
        },
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      closedAt: {
        label: (
          <span className={lineClamp}>
            {t("project.fault_events.table.closed.metadata.end_date")}
          </span>
        ),
        render: (b: Event) => {
          if (!b.closedAt) throw new Error("closedAt cannot be undefined in the closed table");
          const duration = new Date().getTime() - new Date(b.closedAt).getTime();
          const fractional = millisecondsToFractional(duration);
          const date = formatDate(b.closedAt, "date", project.timezone);
          return (
            <div className={datetime}>
              <span>{date}</span>
              <span>{t("datetime.fractional", { type: "date", ...fractional })}</span>
            </div>
          );
        },
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
      },
      reparability: {
        label: (
          <div className={lineClamp}>
            {t("project.fault_events.table.live.metadata.reparability")}{" "}
          </div>
        ),
        render: (b: Event) =>
          b.reparability ? (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={styledOverlayWrapper}
                  text={
                    <Text className={textActionTooltip}>
                      {t("reparability", b.reparability, translations)}
                    </Text>
                  }
                />
              }
            >
              <div>
                <Text className={textAction}>
                  {t("reparability", b.reparability, translations)}
                </Text>
              </div>
            </TooltipsV2>
          ) : null,
        minWidth: "200px",
        maxWidth: "200px",
        sortable: true,
        hidden: hiddenReparabilty,
      },
      lastComment: {
        label: "",
        render: (faultEvent: Event) => {
          if (faultEvent.type === "MANUAL") return <></>;

          const lastComment = lastCommentsByEvent[faultEvent.id];
          if (lastComment === undefined) return <></>;
          return (
            <TooltipsV2
              placement="bottom"
              overlay={
                <OverlayWrapper
                  className={overlayWrapperLastComment}
                  text={
                    <div>
                      <Text type="h4" className={tooltipTitleLastComment}>
                        {t("project.fault_events.table.live.metadata.last_comment.tooltip.title")}
                      </Text>
                      {t("project.fault_events.table.live.metadata.last_comment.tooltip.content", {
                        analysis:
                          lastComment.analysisId && lastComment.metadata
                            ? analysisTitleShort(
                                {
                                  date: lastComment.metadata.date,
                                  group: lastComment.metadata.group,
                                  label: lastComment.metadata.label,
                                },
                                project.timezone,
                              )
                            : null,
                        author: lastComment.author.name ?? lastComment.author.id,
                        publishedAt: formatDate(lastComment.publishedAt, "relative", null),
                        content: (
                          <Markdown
                            config={{
                              ...DEFAULT_CONFIG,
                              theme: {
                                ...DEFAULT_CONFIG.theme,
                                paragraph: "paragraph-body2",
                              },
                            }}
                            className={actionTooltip}
                            defaultValue={lastComment.content}
                          />
                        ),
                      })}
                    </div>
                  }
                />
              }
            >
              <div className={iconComment}>
                <ChatFollowUpLine size="m" color={color.carbon800} />
              </div>
            </TooltipsV2>
          );
        },
        minWidth: "50px",
        maxWidth: "50px",
        alignment: "right",
        sortable: false,
      },
    }),
    [project, translations, hiddenReparabilty, lastCommentsByEvent],
  );

  const gotoEvent = React.useCallback(
    (event: Event, e: React.MouseEvent) => {
      const targetPath = gotoEventPage(event);
      if (targetPath === null) return;

      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath, { state: { from: pathname } });
      }
    },
    [navigate, pathname],
  );

  return (
    <Table
      stickyHeader
      loading={loading}
      datas={sortedDatas}
      metadata={metadata}
      computeRowKey={computeRowKey}
      sort={sort}
      onSort={setSort}
      onRowClick={gotoEvent}
    />
  );
};
export default ClosedEventTable;

//#region CSS
const nameWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  overflow: auto;

  & > :first-child {
    flex: 1;
  }
`;
const datetime = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};

  span:last-child {
    font: ${fonts.body2};
    color: ${colors.carbon700};
  }
`;
const lineClamp = css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @supports (-webkit-line-clamp: 2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const styledOverlayWrapper = css`
  max-width: 350px;
`;
const textAction = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  -webkit-box-orient: vertical;
`;
const textActionTooltip = css`
  color: ${color.hydrogen};
`;
const actionTooltip = css`
  [data-lexical-editor] {
    color: ${color.hydrogen};
  }
`;
const tooltipTitleLastComment = css`
  margin-bottom: ${spacings.xs};
`;
const overlayWrapperLastComment = css`
  width: 286px;
`;
const iconComment = css`
  display: grid;
  place-items: center;
  padding: 10px 5px;
`;
//#endregion CSS
